/**
 * /dashboard/posh/complaints/$id — Complainant's OWN complaint detail.
 *
 * GET /posh/my-complaints/{id}. NOT 2FA-gated, hard-scoped server-side
 * to complainant_id = the authed user.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/posh/complaints/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useMyComplaint } from "@/hooks/use-posh";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { ComplaintDetailView } from "@/components/posh/complaint-detail-view";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/posh/complaints/$id/")({
  component: MyComplaintDetailRoute,
});

function MyComplaintDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const q = useMyComplaint(numId);

  return (
    <PoshModuleShell
      title="Complaint"
      description="Your complaint and its progress. Only you and the committee can see this."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/posh/complaints" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <PoshFeatureGate>
        {q.isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}
        {q.isError && (
          <Alert variant="destructive">
            <AlertTitle>Could not load this complaint</AlertTitle>
            <AlertDescription>
              {toApiError(q.error).message}
            </AlertDescription>
          </Alert>
        )}
        {q.data && (
          <ComplaintDetailView data={q.data} canDownload={false} ownView />
        )}
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
